import { Controller } from "@hotwired/stimulus"
import { formatNumber, getUserLocale } from "../../utils/number-format.util"

// Connects to data-controller="documents--exchange-rate"
export default class extends Controller {
  static targets = ["currency", "exchangeRate", "issueDate", "deliveryDate"]
  static values = {
    baseCurrency: {
      type: String,
      default: "EUR"
    }
  }

  initialize() {
    // Create bound handler once so removeEventListener can match it
    this.boundFetchRate = this.fetchRate.bind(this)
  }

  connect() {
    // Set up event listeners
    if (this.hasIssueDateTarget) {
      this.issueDateTarget.addEventListener("change", this.boundFetchRate)
    }
    if (this.hasDeliveryDateTarget) {
      this.deliveryDateTarget.addEventListener("change", this.boundFetchRate)
    }
    if (this.hasCurrencyTarget) {
      this.currencyTarget.addEventListener("change", this.boundFetchRate)
    }
  }

  disconnect() {
    // Clean up event listeners
    if (this.hasIssueDateTarget) {
      this.issueDateTarget.removeEventListener("change", this.boundFetchRate)
    }
    if (this.hasDeliveryDateTarget) {
      this.deliveryDateTarget.removeEventListener("change", this.boundFetchRate)
    }
    if (this.hasCurrencyTarget) {
      this.currencyTarget.removeEventListener("change", this.boundFetchRate)
    }
  }

  // The case day the rate is anchored to (§ 26 zákona č. 222/2004 Z. z.): the
  // delivery date (dátum dodania) when the form has one, else the issue date —
  // the same fallback FetchDocumentExchangeRateJob applies server-side. The
  // server then offsets it per jurisdiction (SK: preceding day, CZ: same day).
  caseDate() {
    const deliveryDate = this.hasDeliveryDateTarget ? this.deliveryDateTarget.value : ""
    const issueDate = this.hasIssueDateTarget ? this.issueDateTarget.value : ""
    return deliveryDate || issueDate || null
  }

  async fetchRate() {
    const caseDate = this.caseDate()
    const currency = this.hasCurrencyTarget ? this.currencyTarget.value : null
    const baseCurrency = this.baseCurrencyValue || "EUR"

    // Skip if required fields are missing
    if (!caseDate || !currency) {
      // Clear exchange rate if currency changed but date is missing
      if (currency && !caseDate && this.hasExchangeRateTarget) {
        this.exchangeRateTarget.value = ""
      }
      return
    }

    // Skip if currency is the same as base currency
    if (currency.toUpperCase() === baseCurrency.toUpperCase()) {
      if (this.hasExchangeRateTarget) {
        this.exchangeRateTarget.value = this.#formatExchangeRate(1.0)
      }
      return
    }

    // Show loading state
    if (this.hasExchangeRateTarget) {
      this.exchangeRateTarget.disabled = true
      this.exchangeRateTarget.value = ""
    }

    try {
      const url = `/exchange_rates?date=${encodeURIComponent(caseDate)}&from_currency=${encodeURIComponent(baseCurrency)}&to_currency=${encodeURIComponent(currency)}`
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      const data = await response.json()

      if (!response.ok) {
        // Handle error responses - Ruby controller always returns { error: "..." }
        if (this.hasExchangeRateTarget) {
          this.exchangeRateTarget.value = ""
        }
        return
      }

      // Success response - Ruby controller returns { rate: number }
      if (data.rate != null && this.hasExchangeRateTarget) {
        const rate = parseFloat(data.rate)
        if (!Number.isNaN(rate)) {
          this.exchangeRateTarget.value = this.#formatExchangeRate(rate)
        } else {
          this.exchangeRateTarget.value = ""
        }
      }
    } catch (error) {
      console.error("Error fetching exchange rate:", error)
      if (this.hasExchangeRateTarget) {
        this.exchangeRateTarget.value = ""
      }
    } finally {
      if (this.hasExchangeRateTarget) {
        this.exchangeRateTarget.disabled = false
      }
    }
  }

  // Format exchange rate number according to current locale
  #formatExchangeRate(rate) {
    const locale = this.#getCurrentLocale()
    return formatNumber(rate, locale, {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 4
    })
  }

  // Get current locale from select field or fall back to locale value
  #getCurrentLocale() {
    // Check if exchange rate field has document-type-value (for documents)
    if (this.hasExchangeRateTarget) {
      const documentType = this.exchangeRateTarget.getAttribute("data-documents--number-format-document-type-value")
      if (documentType) {
        const localeSelect = document.querySelector(`#${documentType}_locale`)
        if (localeSelect?.value) {
          return localeSelect.value
        }
      }
    }

    // Check if exchange rate field has locale-value (for expenses)
    if (this.hasExchangeRateTarget) {
      const localeValue = this.exchangeRateTarget.getAttribute("data-documents--number-format-locale-value")
      if (localeValue) {
        // Still try to get current value from select if available
        const localeSelect = document.querySelector('[id*="_locale"]')
        if (localeSelect?.value) {
          return localeSelect.value
        }
        return localeValue
      }
    }

    // Fallback: try to find any locale select
    const localeSelect = document.querySelector('[id*="_locale"]')
    if (localeSelect?.value) {
      return localeSelect.value
    }

    // Final fallback to meta tag
    return getUserLocale()
  }
}
